import DatePicker from "@rio-cloud/rio-uikit/components/datepicker/DatePicker"
import React, { useMemo, useState, useEffect, useRef } from "react"
import { useDateFilterStore } from "../../services/dateFilterStore";
import { debounce } from 'lodash';
import type { Moment } from 'moment';
import moment from "moment";

const DEBOUNCE_DELAY = 3000; 
const MAX_DATE_RANGE_DAYS = 30;
const MIN_DATE_RANGE_DAYS = 1;

export const DateFilter = () => {
    const { 
        setStartDate: originalSetStartDate, 
        setEndDate: originalSetEndDate, 
        startDate: storeStartDate, 
        endDate: storeEndDate 
    } = useDateFilterStore();

    const [localStartDate, setLocalStartDate] = useState(storeStartDate);
    const [localEndDate, setLocalEndDate] = useState(storeEndDate);

    const isInitialLoad = useRef(true);

    useEffect(() => { setLocalStartDate(storeStartDate); }, [storeStartDate]);
    useEffect(() => { setLocalEndDate(storeEndDate); }, [storeEndDate]);
    
    useEffect(() => {
        if (isInitialLoad.current && storeStartDate && moment(storeStartDate).isValid()) {
            const currentMoment = moment(storeStartDate);
            if (currentMoment.hour() !== 0 || currentMoment.minute() !== 0 || currentMoment.second() !== 0 || currentMoment.millisecond() !== 0) {
                const normalizedStart = currentMoment.clone().startOf('day');
                originalSetStartDate(normalizedStart.toDate().toISOString()); 
            }
        }
        isInitialLoad.current = false;
    }, [originalSetStartDate, storeStartDate]);

    const debouncedSetStartDate = useMemo(
        () => debounce((dateString: string) => { originalSetStartDate(dateString); }, DEBOUNCE_DELAY),
        [originalSetStartDate]
    );

    const debouncedSetEndDate = useMemo(
        () => debounce((dateString: string) => { originalSetEndDate(dateString); }, DEBOUNCE_DELAY),
        [originalSetEndDate]
    );

    
    const dateStartValidator = (current: Moment) => {
        const todayLimit = moment().endOf('day'); 
        return current.isSameOrBefore(todayLimit); 
    }

    const dateEndValidator = (current: Moment) => {
        const upperLimit = moment().endOf('day'); 
        
        if (localStartDate && moment(localStartDate).isValid()) {
            const lowerLimit = moment(localStartDate); 
            return current.isSameOrAfter(lowerLimit) && current.isSameOrBefore(upperLimit);
        }
        
        return current.isSameOrBefore(upperLimit);
    }


    const handleStartDateChange = (result: Moment | string) => {
        if (typeof result !== 'string' && result.isValid()) {
            const newStartDateMoment = result;
            const newStartDateISO = newStartDateMoment.toDate().toISOString();
            const currentEndDateMoment = localEndDate ? moment(localEndDate) : null;
            
            setLocalStartDate(newStartDateISO);
            debouncedSetStartDate(newStartDateISO);

            if (currentEndDateMoment) {
                const diffInDays = currentEndDateMoment.diff(newStartDateMoment, 'days');
                
                if (newStartDateMoment.isSameOrAfter(currentEndDateMoment)) {
                    
                    const adjustedEndDateMoment = newStartDateMoment.clone().add(MAX_DATE_RANGE_DAYS, 'days'); 
                    
                    const finalEndDateMoment = adjustedEndDateMoment.isAfter(moment()) ? moment().endOf('day') : adjustedEndDateMoment;
                    const finalEndDateISO = finalEndDateMoment.toDate().toISOString();

                    setLocalEndDate(finalEndDateISO);
                    debouncedSetEndDate(finalEndDateISO);
                    
                } else if (diffInDays > MAX_DATE_RANGE_DAYS) {
                    
                    const adjustedEndDateMoment = newStartDateMoment.clone().add(MAX_DATE_RANGE_DAYS, 'days');
                    
                    const finalEndDateMoment = adjustedEndDateMoment.isAfter(moment()) ? moment().endOf('day') : adjustedEndDateMoment;
                    const finalEndDateISO = finalEndDateMoment.toDate().toISOString();

                    setLocalEndDate(finalEndDateISO);
                    debouncedSetEndDate(finalEndDateISO);
                }
            }
        }
    };
    
    const handleEndDateChange = (result: Moment | string) => {
        if (typeof result !== 'string' && result.isValid()) {
            const newEndDateMoment = result;
            const newEndDateISO = newEndDateMoment.toDate().toISOString();
            const currentStartDateMoment = localStartDate ? moment(localStartDate) : null;
            
            setLocalEndDate(newEndDateISO);
            debouncedSetEndDate(newEndDateISO);

            if (currentStartDateMoment) {
                const diffInDays = newEndDateMoment.diff(currentStartDateMoment, 'days');

                if (newEndDateMoment.isSameOrBefore(currentStartDateMoment)) { 
                    
                    const adjustedStartDateMoment = newEndDateMoment.clone()
                        .subtract(MIN_DATE_RANGE_DAYS, 'days');
                    
                    const adjustedStartDateISO = adjustedStartDateMoment.toDate().toISOString();
                    
                    setLocalStartDate(adjustedStartDateISO);
                    debouncedSetStartDate(adjustedStartDateISO);

                } else if (diffInDays > MAX_DATE_RANGE_DAYS) {
                    
                    const adjustedStartDateMoment = newEndDateMoment.clone()
                        .subtract(MAX_DATE_RANGE_DAYS, 'days');
                    
                    const adjustedStartDateISO = adjustedStartDateMoment.toDate().toISOString();
                    
                    setLocalStartDate(adjustedStartDateISO);
                    debouncedSetStartDate(adjustedStartDateISO);
                }
            }
        }
    };

    return (
        <React.Fragment>
            <DatePicker 
                inputProps={{ placeholder: 'Data de Início', disabled: false }}  
                isValidDate={dateStartValidator}
                value={localStartDate ? moment(localStartDate) : moment()} 
                onChange={handleStartDateChange} 
                dateFormat="DD/MM/YYYY" 
                timeFormat="HH:mm" 
                locale="pt-br" 
                className='min-width-200 margin-bottom-0' 
            />
            
            <DatePicker 
                inputProps={{ placeholder: 'Data de Fim', disabled: false }} 
                isValidDate={dateEndValidator}
                value={localEndDate ? moment(localEndDate) : moment()} 
                onChange={handleEndDateChange} 
                dateFormat="DD/MM/YYYY" 
                timeFormat="HH:mm" 
                locale="pt-br" 
                className='min-width-200 margin-bottom-0' 
            />
        </React.Fragment>
    )
}